// Liste des réclamations : filtres (état, sensible, retard, service), indicateur sensible et
// rappel de l'accès restreint (une réclamation sensible n'est visible que des personnes
// habilitées ; une personne non habilitée ne la voit nulle part), lien vers les statistiques.
import { Bandeau, EtatVide, Filtres, Lien, LienBouton, PuceEtat, Tableau, useFiltresPersistes, type ColonneTableau } from '@cc/ui';
import { BarChart3, MessageSquareWarning } from 'lucide-react';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { date } from '../../commun/format';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { useServices } from '../demarches/contenu';
import { LIBELLES_ETAT_RECLAMATION, useReclamations, type FiltresReclamations, type Reclamation } from './donnees';
import commun from '../../commun/commun.module.css';

export function puceReclamation(r: Pick<Reclamation, 'etat' | 'en_retard'>) {
  if (r.en_retard) return <PuceEtat ton="retard" libelle="En retard" />;
  const libelle = LIBELLES_ETAT_RECLAMATION[r.etat] ?? r.etat;
  if (r.etat === 'cloturee') return <PuceEtat ton="ok" libelle={libelle} />;
  if (r.etat === 'repondue') return <PuceEtat ton="ok" libelle={libelle} />;
  if (r.etat === 'en_instruction') return <PuceEtat ton="attente" libelle={libelle} />;
  return <PuceEtat ton="neutre" libelle={libelle} />;
}

export function PageReclamations() {
  const { contexte } = useSession();
  const [filtres, setFiltres] = useFiltresPersistes('reclamations', { etat: '', sensible: '', en_retard: '', service: '' });
  const f: FiltresReclamations = { etat: filtres.etat ?? '', sensible: filtres.sensible ?? '', en_retard: filtres.en_retard ?? '', service: filtres.service ?? '' };
  const liste = useReclamations(f);
  const services = useServices();
  const habilite = peut(contexte, 'reclamation_sensible', 'voir');

  const colonnes: ColonneTableau<Reclamation>[] = [
    { id: 'numero', entete: 'Numéro', enTeteLigne: true, masquable: false, valeurTri: (r) => r.numero, cellule: (r) => <Lien href={`/reclamations/${r.id}`} className={commun.reference}>{r.numero}</Lien> },
    { id: 'categorie', entete: 'Catégorie', cellule: (r) => r.categorie_libelle, valeurTri: (r) => r.categorie_libelle },
    { id: 'demandeur', entete: 'Demandeur', cellule: (r) => r.demandeur.nom ?? 'non renseigné' },
    { id: 'service', entete: 'Service', cellule: (r) => r.service?.nom ?? 'non affectée' },
    { id: 'sensible', entete: 'Sensible', cellule: (r) => (r.sensible ? <PuceEtat ton="retard" libelle="Sensible, accès restreint" /> : 'Non'), valeurTri: (r) => (r.sensible ? 1 : 0) },
    { id: 'recue', entete: 'Reçue le', cellule: (r) => date(r.cree_le), valeurTri: (r) => r.cree_le },
    { id: 'echeance', entete: 'Échéance', cellule: (r) => date(r.echeance), valeurTri: (r) => r.echeance },
    { id: 'etat', entete: 'État', cellule: puceReclamation, valeurTri: (r) => r.etat },
  ];

  return (
    <>
      <EnTetePage
        etiquette="Réclamations et satisfaction"
        titre="Réclamations"
        description="Réclamations déposées par les usagers dans le portail. Échéance de réponse en jours ouvrés ; l'usager évalue la réponse de 1 à 5."
        actions={
          <LienBouton href="/reclamations/statistiques" icone={BarChart3}>
            Statistiques
          </LienBouton>
        }
      />
      <Bandeau type="info">
        {habilite
          ? 'Vous êtes habilité aux réclamations sensibles : elles apparaissent dans cette liste et ne peuvent être affectées qu\'à des personnes habilitées.'
          : 'Les réclamations sensibles (comportement d\'un agent, corruption alléguée, données personnelles, ou marquées sensibles) ne sont visibles que des personnes disposant d\'une habilitation nominative validée : elles n\'apparaissent pas dans cette liste.'}
      </Bandeau>
      <Filtres
        libelle="Filtres des réclamations"
        groupes={[
          { id: 'etat', libelle: 'État', options: [{ valeur: '', libelle: 'Toutes' }, ...Object.entries(LIBELLES_ETAT_RECLAMATION).map(([valeur, libelle]) => ({ valeur, libelle }))] },
          { id: 'en_retard', libelle: 'Délai', options: [{ valeur: '', libelle: 'Tous' }, { valeur: 'oui', libelle: 'En retard' }, { valeur: 'non', libelle: 'Dans le délai' }] },
          { id: 'sensible', libelle: 'Sensible', options: [{ valeur: '', libelle: 'Toutes' }, { valeur: 'oui', libelle: 'Sensibles' }, { valeur: 'non', libelle: 'Non sensibles' }] },
          { id: 'service', libelle: 'Service', options: [{ valeur: '', libelle: 'Tous' }, ...(services.data ?? []).map((s) => ({ valeur: s.id, libelle: s.nom }))] },
        ]}
        valeurs={filtres}
        onChanger={setFiltres}
      />
      <EtatRequete chargement={liste.isPending} erreur={liste.error} onReessayer={() => void liste.refetch()} lot="L1-A" fonction="La liste des réclamations">
        {(liste.data ?? []).length === 0 ? (
          <EtatVide titre="Aucune réclamation" icone={MessageSquareWarning}>
            <p>Aucune réclamation visible ne correspond à ces filtres.</p>
          </EtatVide>
        ) : (
          <Tableau legende="Réclamations" colonnes={colonnes} lignes={liste.data ?? []} cleLigne={(r) => r.id} cleColonnes="reclamations" />
        )}
      </EtatRequete>
    </>
  );
}
